import { type MutableRefObject, type RefObject, useEffect } from 'react'

import type { TagAutocompleteHandle } from './TagAutocomplete.types'

interface UseTagKeyboardHandlersOptions {
  mode: 'read' | 'edit'
  inputText: string
  inputRef: RefObject<HTMLInputElement>
  autocompleteRef: RefObject<TagAutocompleteHandle | null>
  handleSubmitRef: MutableRefObject<() => void>
  isMutating: boolean
  exitEditMode: () => void
}

export const useTagKeyboardHandlers = (
  options: UseTagKeyboardHandlersOptions,
) => {
  const {
    mode,
    inputText,
    inputRef,
    autocompleteRef,
    handleSubmitRef,
    isMutating,
    exitEditMode,
  } = options

  useEffect(() => {
    if (mode !== 'edit') {
      return
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      if (
        e.key === 'Escape' &&
        inputText.trim() &&
        autocompleteRef.current?.hasSuggestions()
      ) {
        autocompleteRef.current.clearSuggestions()
        inputRef.current?.focus()

        return
      }

      if (
        e.key === 'ArrowDown' &&
        document.activeElement === inputRef.current &&
        autocompleteRef.current?.hasSuggestions()
      ) {
        e.preventDefault()
        autocompleteRef.current.focusFirst()

        return
      }

      if (
        document.activeElement === inputRef.current &&
        autocompleteRef.current?.handleKeyDown(e)
      ) {
        return
      }

      if (e.key === 'Enter' && document.activeElement === inputRef.current) {
        e.preventDefault()
        handleSubmitRef.current()

        return
      }

      if (e.key === 'Escape' && !isMutating) {
        exitEditMode()
      }
    }

    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [
    mode,
    inputText,
    inputRef,
    autocompleteRef,
    handleSubmitRef,
    isMutating,
    exitEditMode,
  ])
}
